import React, { useState, useEffect, useRef } from 'react';
import { InventoryCompound, CompoundIntelligence, InventoryBatch, InventorySample, InventoryLocation } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryApi';
import apiClient from '../../api/client';

interface Props {
  compound: InventoryCompound;
  onClose: () => void;
  autoEditStock?: boolean;
}

const ZONE_COLORS: Record<number, string> = {
  1: '#EF4444', // Literal Match - red
  2: '#F97316', // Chemical Isostere - orange
  3: '#F59E0B', // Biological Isostere - amber
  4: '#3B82F6', // Close Analog - blue
  5: '#10B981', // Novel - green
};

const CompoundDetailPanel: React.FC<Props> = ({ compound, onClose, autoEditStock = false }) => {
  const [intelligence, setIntelligence] = useState<CompoundIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<InventoryCompound | null>(null);
  const [structureSvg, setStructureSvg] = useState('');
  const [expandedBatch, setExpandedBatch] = useState<number | null>(null);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [editingSampleId, setEditingSampleId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editUnit, setEditUnit] = useState('mg');
  const [editContainer, setEditContainer] = useState('vial');
  const [editLocationId, setEditLocationId] = useState('');
  const [editStatus, setEditStatus] = useState('available');
  const [editExpiry, setEditExpiry] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editError, setEditError] = useState('');
  const [savingSample, setSavingSample] = useState(false);
  const autoEditHandled = useRef(false);

  // Load intelligence data
  useEffect(() => {
    setLoading(true);
    inventoryAPI.getIntelligence(compound.compound_id)
      .then(res => setIntelligence(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [compound.compound_id]);

  // Load full compound detail (with batches/samples)
  useEffect(() => {
    inventoryAPI.getCompound(compound.compound_id)
      .then(res => setDetail(res.data))
      .catch(() => {});
  }, [compound.compound_id]);

  useEffect(() => {
    inventoryAPI.getLocationTree()
      .then(res => setLocations(flattenLocations(res.data)))
      .catch(() => setLocations([]));
  }, []);

  // Load structure SVG
  useEffect(() => {
    if (!compound.canonical_smiles) return;
    apiClient.post('/v2/indigo/render', {
      structure: { value: compound.canonical_smiles, type: 'RAW' },
      width: '400px',
      height: '250px',
    }, { responseType: 'text' })
      .then(res => setStructureSvg(res.data))
      .catch(() => {});
  }, [compound.canonical_smiles]);

  const beginSampleEdit = (sample: InventorySample) => {
    setEditingSampleId(sample.sample_id);
    setEditAmount(sample.current_amount == null ? '' : String(sample.current_amount));
    setEditUnit(sample.amount_unit || 'mg');
    setEditContainer(sample.container_type || 'vial');
    setEditLocationId(sample.location_id == null ? '' : String(sample.location_id));
    setEditStatus(sample.status || 'available');
    setEditExpiry(sample.expiry_date ? sample.expiry_date.slice(0, 10) : '');
    setEditNotes(sample.notes || '');
    setEditError('');
  };

  useEffect(() => {
    if (!autoEditStock || autoEditHandled.current || !detail?.batches) return;
    const batch = detail.batches.find(item => item.samples && item.samples.length > 0);
    const sample = batch?.samples?.[0];
    if (!batch || !sample) return;

    autoEditHandled.current = true;
    setExpandedBatch(batch.batch_id);
    beginSampleEdit(sample);
  }, [autoEditStock, detail]);

  const saveSampleEdit = async (sampleId: number) => {
    const amount = Number(editAmount);
    if (editAmount.trim() === '' || !Number.isFinite(amount) || amount < 0) {
      setEditError('Current amount is required and cannot be negative.');
      return;
    }

    setSavingSample(true);
    setEditError('');
    try {
      await inventoryAPI.updateSample(sampleId, {
        current_amount: amount,
        amount_unit: editUnit,
        container_type: editContainer,
        location_id: editLocationId ? Number(editLocationId) : undefined,
        status: editStatus,
        expiry_date: editExpiry || undefined,
        notes: editNotes.trim() || undefined,
      });
      const refreshed = await inventoryAPI.getCompound(compound.compound_id);
      setDetail(refreshed.data);
      setEditingSampleId(null);
    } catch (err: any) {
      setEditError(err.response?.data?.detail || 'Failed to update stock.');
    } finally {
      setSavingSample(false);
    }
  };

  return (
    <div style={{
      width: 480,
      flexShrink: 0,
      borderLeft: '1px solid var(--card-border)',
      backgroundColor: 'var(--card-bg)',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--card-border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0D9488' }}>
            {compound.chemistry_code}
          </div>
          {compound.compound_name && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {compound.compound_name}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 20, padding: '4px 8px',
          }}
        >
          &times;
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 20px' }}>
        {/* Structure */}
        {structureSvg && (
          <div style={{
            padding: '16px 0',
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid var(--card-border)',
          }}>
            <div dangerouslySetInnerHTML={{ __html: structureSvg }} style={{ maxWidth: 400 }} />
          </div>
        )}

        {/* Identity */}
        <Section title="Identity">
          <InfoRow label="Inventory Code" value={compound.chemistry_code} />
          {compound.registry_code && (
            <InfoRow label="Registry Code" value={compound.registry_code} />
          )}
          <InfoRow label="Category" value={(compound.category || 'compound').replace(/_/g, ' ')} />
          <InfoRow label="SMILES" value={compound.canonical_smiles} mono />
          <InfoRow label="InChIKey" value={compound.inchi_key} mono />
          <InfoRow label="Formula" value={compound.mol_formula} />
          <InfoRow label="MW" value={compound.mol_weight?.toFixed(2)} />
          <InfoRow label="Status" value={compound.compound_status} />
        </Section>

        {/* Intelligence — Patent Landscape */}
        <Section title="Patent Landscape">
          {loading ? (
            <LoadingSpinner label="Searching patent database..." />
          ) : intelligence?.patents && intelligence.patents.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{
                padding: '8px 12px',
                borderRadius: 6,
                backgroundColor: intelligence.ip_status === 'blocked' ? 'rgba(239,68,68,0.1)' :
                  intelligence.ip_status === 'caution' ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
                color: intelligence.ip_status === 'blocked' ? '#EF4444' :
                  intelligence.ip_status === 'caution' ? '#F59E0B' : '#10B981',
                fontSize: 13, fontWeight: 600, textTransform: 'capitalize' as any,
              }}>
                IP Status: {intelligence.ip_status} ({intelligence.patent_count} matches)
              </div>
              {intelligence.patents.slice(0, 10).map((p, i) => (
                <div key={i} style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: `1px solid ${ZONE_COLORS[p.zone] || '#6B7280'}20`,
                  backgroundColor: `${ZONE_COLORS[p.zone] || '#6B7280'}08`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: ZONE_COLORS[p.zone] || '#6B7280' }}>
                      {p.zone_label}
                    </span>
                    {p.similarity != null && !isNaN(p.similarity) && (
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {(p.similarity * 100).toFixed(0)}% similar
                    </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, fontFamily: 'monospace' }}>
                    {p.surechembl_id}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#10B981', fontSize: 13 }}>
              No patent collisions detected. Novel chemical space.
            </div>
          )}
        </Section>

        {/* Bioactivity */}
        <Section title="Bioactivity">
          {loading ? (
            <LoadingSpinner label="Searching ChEMBL..." />
          ) : intelligence?.bioactivities && intelligence.bioactivities.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                    <th style={thStyle}>Target</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Value</th>
                    <th style={thStyle}>pChEMBL</th>
                  </tr>
                </thead>
                <tbody>
                  {intelligence.bioactivities.slice(0, 15).map((b, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--card-border)' }}>
                      <td style={tdStyle}>{b.target_name || '-'}</td>
                      <td style={tdStyle}>{b.standard_type || '-'}</td>
                      <td style={tdStyle}>
                        {b.standard_relation}{b.standard_value?.toFixed(1)} {b.standard_units}
                      </td>
                      <td style={tdStyle}>
                        {b.pchembl_value?.toFixed(1) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              No bioactivity data found in ChEMBL.
            </div>
          )}
        </Section>

        {/* Targets & Pathways */}
        <Section title="Target / Pathway Context">
          {loading ? (
            <LoadingSpinner label="Searching targets..." />
          ) : (
            <>
              {intelligence?.targets && intelligence.targets.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Protein Targets ({intelligence.target_count})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {intelligence.targets.map((t, i) => (
                      <span key={i} style={{
                        padding: '3px 8px', borderRadius: 12,
                        backgroundColor: 'rgba(13,148,136,0.1)', color: '#0D9488',
                        fontSize: 11, fontWeight: 500,
                      }}>
                        {t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {intelligence?.pathways && intelligence.pathways.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Pathways ({intelligence.pathway_count})
                  </div>
                  {intelligence.pathways.map((pw, i) => (
                    <div key={i} style={{
                      padding: '4px 0', fontSize: 12,
                      borderBottom: i < intelligence.pathways.length - 1 ? '1px solid var(--card-border)' : 'none',
                    }}>
                      <span style={{ color: 'var(--text-primary)' }}>{pw.pathway_name}</span>
                      {pw.source && (
                        <span style={{ color: 'var(--text-secondary)', marginLeft: 6, fontSize: 10 }}>
                          [{pw.source}]
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(!intelligence?.targets?.length && !intelligence?.pathways?.length) && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  No target/pathway data available.
                </div>
              )}
            </>
          )}
        </Section>

        {/* Batches */}
        <Section title="Batches & Samples">
          {detail?.batches && detail.batches.length > 0 ? (
            detail.batches.map((batch: InventoryBatch) => (
              <div key={batch.batch_id} style={{
                marginBottom: 8,
                border: '1px solid var(--card-border)',
                borderRadius: 6,
                overflow: 'hidden',
              }}>
                <div
                  onClick={() => setExpandedBatch(expandedBatch === batch.batch_id ? null : batch.batch_id)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {batch.batch_code}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {batch.purity_percent != null ? `${batch.purity_percent}% purity` : ''}
                    {batch.supplier ? ` | ${batch.supplier}` : ''}
                  </span>
                </div>
                {expandedBatch === batch.batch_id && batch.samples && (
                  <div style={{ padding: '8px 12px' }}>
                    {batch.samples.length > 0 ? batch.samples.map(s => (
                      <React.Fragment key={s.sample_id}>
                        <div style={{
                          padding: '8px 0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '1px solid var(--card-border)',
                          fontSize: 12,
                          gap: 8,
                        }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.barcode}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 2 }}>
                              {s.location_name || 'No storage location'}
                              {s.container_type ? ` · ${s.container_type.replace(/_/g, ' ')}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <span>
                              <StatusBadge status={s.status} />
                              {s.current_amount != null && (
                                <span style={{ marginLeft: 8 }}>{s.current_amount} {s.amount_unit}</span>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => editingSampleId === s.sample_id ? setEditingSampleId(null) : beginSampleEdit(s)}
                              style={smallButtonStyle}
                            >
                              {editingSampleId === s.sample_id ? 'Cancel' : 'Edit stock'}
                            </button>
                          </div>
                        </div>
                        {editingSampleId === s.sample_id && (
                          <div style={{
                            padding: 12, margin: '8px 0 12px', borderRadius: 6,
                            border: '1px solid rgba(13,148,136,0.35)',
                            backgroundColor: 'rgba(13,148,136,0.05)',
                          }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--text-primary)' }}>
                              Edit physical stock
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <div>
                                <label style={editLabelStyle}>Current amount *</label>
                                <input type="number" min="0" step="0.1" value={editAmount} onChange={e => setEditAmount(e.target.value)} style={editInputStyle} />
                              </div>
                              <div>
                                <label style={editLabelStyle}>Unit</label>
                                <select value={editUnit} onChange={e => setEditUnit(e.target.value)} style={editInputStyle}>
                                  {['mg', 'g', 'kg', 'mL', 'L'].map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                </select>
                              </div>
                              <div>
                                <label style={editLabelStyle}>Container</label>
                                <select value={editContainer} onChange={e => setEditContainer(e.target.value)} style={editInputStyle}>
                                  <option value="vial">Vial</option>
                                  <option value="tube">Tube</option>
                                  <option value="plate_well">Plate well</option>
                                  <option value="bottle">Bottle</option>
                                  <option value="bag">Bag</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label style={editLabelStyle}>Status</label>
                                <select value={editStatus} onChange={e => setEditStatus(e.target.value)} style={editInputStyle}>
                                  <option value="available">Available</option>
                                  {s.status === 'reserved' && <option value="reserved">Reserved</option>}
                                  <option value="in_use">In use</option>
                                  <option value="depleted">Depleted</option>
                                  <option value="expired">Expired</option>
                                  <option value="disposed">Disposed</option>
                                </select>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={editLabelStyle}>Storage location</label>
                                <select value={editLocationId} onChange={e => setEditLocationId(e.target.value)} style={editInputStyle}>
                                  <option value="">No location</option>
                                  {locations.map(location => (
                                    <option key={location.location_id} value={location.location_id}>
                                      {location.materialised_path || location.name}
                                      {location.temperature_zone ? ` (${location.temperature_zone})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={editLabelStyle}>Expiry date</label>
                                <input type="date" value={editExpiry} onChange={e => setEditExpiry(e.target.value)} style={editInputStyle} />
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={editLabelStyle}>Adjustment notes</label>
                                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Reason for quantity or stock change" style={{ ...editInputStyle, minHeight: 58, resize: 'vertical' }} />
                              </div>
                            </div>
                            {editError && (
                              <div role="alert" style={{ color: '#EF4444', fontSize: 11, marginTop: 8 }}>{editError}</div>
                            )}
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                              <button type="button" onClick={() => saveSampleEdit(s.sample_id)} disabled={savingSample} style={{ ...smallButtonStyle, backgroundColor: '#0D9488', color: '#fff', borderColor: '#0D9488' }}>
                                {savingSample ? 'Saving...' : 'Save changes'}
                              </button>
                              <button type="button" onClick={() => setEditingSampleId(null)} disabled={savingSample} style={smallButtonStyle}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    )) : (
                      <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No samples</div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              No batches yet.
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

// ── Sub-components ──

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ padding: '16px 0', borderBottom: '1px solid var(--card-border)' }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
      {title}
    </div>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{
      color: 'var(--text-primary)',
      fontFamily: mono ? 'monospace' : 'inherit',
      fontSize: mono ? 11 : 12,
      maxWidth: 280,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: 'right',
    }}>
      {value || '-'}
    </span>
  </div>
);

const LoadingSpinner: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
    <div style={{
      width: 14, height: 14, border: '2px solid var(--card-border)',
      borderTopColor: '#0D9488', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    available: '#10B981',
    reserved: '#F59E0B',
    in_use: '#3B82F6',
    depleted: '#EF4444',
    expired: '#6B7280',
    disposed: '#6B7280',
  };
  return (
    <span style={{
      padding: '2px 6px', borderRadius: 4,
      backgroundColor: `${colors[status] || '#6B7280'}15`,
      color: colors[status] || '#6B7280',
      fontSize: 10, fontWeight: 500, textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', fontWeight: 500,
  color: 'var(--text-secondary)', fontSize: 11,
};

const tdStyle: React.CSSProperties = {
  padding: '6px 8px', color: 'var(--text-primary)',
};

const smallButtonStyle: React.CSSProperties = {
  padding: '4px 8px', borderRadius: 4, border: '1px solid var(--card-border)',
  backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', cursor: 'pointer',
  fontSize: 10, fontWeight: 600,
};

const editLabelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 3, color: 'var(--text-secondary)', fontSize: 10, fontWeight: 600,
};

const editInputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 4,
  border: '1px solid var(--card-border)', backgroundColor: 'var(--input-bg, var(--card-bg))',
  color: 'var(--text-primary)', fontSize: 11,
};

const flattenLocations = (nodes: InventoryLocation[]): InventoryLocation[] => {
  const flattened: InventoryLocation[] = [];
  const visit = (items: InventoryLocation[]) => {
    items.forEach(item => {
      flattened.push(item);
      if (item.children?.length) visit(item.children);
    });
  };
  visit(nodes);
  return flattened;
};

export default CompoundDetailPanel;
